import { useEffect, useRef } from 'react';
import _ from 'lodash';
import { useObserver } from 'mobx-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';

import useUser from 'src/hooks/use-user';
import { usePostSignupModalSequence } from 'src/hooks/use-post-signup-modal-sequence';

export const PostSignupOAuthTrigger = (): JSX.Element | null => {
  const router = useRouter();
  const User = useUser();
  const isUserLoading = useObserver(() => User.loading) ?? true;
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const customerId = useObserver(() => User.id);
  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);
  const flags = useFlags();
  const isRolloutEnabled = flags['fintech.platform.post-signup-onboarding-modals.rollout'] ?? false;
  const { triggerPostSignupModalSequence, isReady: isDispensaryReady } = usePostSignupModalSequence();
  const hasFiredRef = useRef(false);

  useEffect(() => {
    // A false flag reading may just mean flags haven't streamed in yet, so this must have no side effects.
    if (!router?.isReady || hasFiredRef.current || !isRolloutEnabled || !isDispensaryReady) {
      return;
    }

    const { socialAuthSource, isNewUser, skipPostSignupSequence } = router.query;

    if (socialAuthSource !== 'signup') {
      return;
    }

    // routeChangeStart (fired even by a shallow replace) closes every open modal, so the strip
    // must resolve before the modal opens. hasFiredRef is also this effect's replay guard.
    const stripParams = async (): Promise<void> => {
      try {
        await router.replace(
          {
            pathname: router.pathname,
            query: _.omit(router.query, ['socialAuthSource', 'isNewUser', 'skipPostSignupSequence']),
          },
          undefined,
          { shallow: true }
        );
      } catch {
        // navigation cancelled or superseded; the sequence should still run
      }
    };

    if (isNewUser !== 'true') {
      void stripParams();
      return;
    }

    if (isUserLoading) {
      return;
    }

    if (!isLoggedIn || !customerId) {
      void stripParams();
      return;
    }

    const runSequence = async (): Promise<void> => {
      await stripParams();
      void triggerPostSignupModalSequence({
        customerId,
        phone: phone ?? '',
        birthday: birthday ?? '',
        skipPostSignupSequence: skipPostSignupSequence === 'true',
      });
    };

    hasFiredRef.current = true;
    void runSequence();
  }, [
    router,
    isRolloutEnabled,
    isDispensaryReady,
    isUserLoading,
    isLoggedIn,
    customerId,
    phone,
    birthday,
    triggerPostSignupModalSequence,
  ]);

  return null;
};
